import { DateTime } from 'luxon';
import * as models from 'smg/customer-api/models';
import { QuoteRequestProjects, QuoteRequestProjectsCount } from 'smg/customer-api/quoterequestprojects';
import * as session from 'smg/customer-api/session';
import { SetDocumentTitle } from 'smg/dom';
import { Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import { BindClearSearch, PageLoader } from '../account';
import { Relref } from '../hugo';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';
import { ProjectStatusLabel, ProjectStatusPillClasses, ProjectOrdersLine } from '../projects';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const closedView = QueryString().get('closed') === '1';

    if (closedView) {
        const $pageTitle = document.querySelector('#page-title');
        if ($pageTitle) {
            $pageTitle.textContent = 'Closed Projects';
        }
        SetDocumentTitle('Closed Projects');
        document.querySelector('#back-to-projects')?.classList.remove('d-none');

        document.querySelector('#search-form')?.closest('.col')?.classList.add('d-none');
        const $sortWrap = document.querySelector('#sort')?.parentElement;
        $sortWrap?.classList.add('d-none');
        $sortWrap?.previousElementSibling?.classList.add('d-none');
    }

    BindClearSearch();

    const $empty = document.querySelector('#projects-empty') as HTMLElement;
    const $noMatch = document.querySelector('#projects-no-match') as HTMLElement;
    const $closedEmpty = document.querySelector('#projects-closed-empty') as HTMLElement;
    const $closedLinkRow = document.querySelector('#closed-link-row') as HTMLElement;
    const $noMatchTitle = document.querySelector('#no-match-title') as HTMLElement;
    const $noMatchBody = document.querySelector('#no-match-body') as HTMLElement;
    const $emptyTitle = document.querySelector('#projects-empty-title') as HTMLElement;
    const $emptyBody = document.querySelector('#projects-empty-body') as HTMLElement;
    const $emptyCta = document.querySelector('#projects-empty-cta') as HTMLElement;
    const $closedEmptyTitle = document.querySelector('#projects-closed-empty-title') as HTMLElement;
    const $closedEmptyBody = document.querySelector('#projects-closed-empty-body') as HTMLElement;

    let view: OrganizationView;

    const pl: PageLoader<models.QuoteRequestProjectsHyejooProject> = new PageLoader<models.QuoteRequestProjectsHyejooProject>({
        cards: { empty: closedView ? $closedEmpty : $empty, noMatch: $noMatch },
        init: async () => {
            const user = (await session.Session()).user;
            view = await ResolveOrganizationView(user);
            RenderOrganizationSwitch(view, user);
            const $closedLink = document.querySelector('#closed-link-row a') as HTMLAnchorElement | null;
            if ($closedLink) {
                $closedLink.href = view.withLens($closedLink.href);
            }
            if (!closedView) {
                void QuoteRequestProjectsCount('closed:true', view.params()).then(count => {
                    if (count > 0 && pl.state() !== 'failed') {
                        const $closedCount = document.querySelector('#closed-count');
                        if ($closedCount) {
                            $closedCount.textContent = String(count);
                        }
                        $closedLinkRow.classList.remove('d-none');
                    }
                }).catch(() => undefined);
            }
        },
        get: () => QuoteRequestProjects({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'name,updated,balanceDue,closed,closedAt,userID,status,waitingOn,quote,orders',
            p: pl.currentPage(),
            q: [pl.search().trim(), closedView ? 'closed:true' : ''].filter(Boolean).join(' '),
        }, view.params()).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: project => {
            const $card = template.Clone('#project-card-template').firstElementChild as HTMLElement;
            const $thumb = $card.querySelector('.project-thumb') as HTMLImageElement;
            const $placeholder = $card.querySelector('.project-thumb-placeholder') as HTMLElement;
            const $name = $card.querySelector('.project-name') as HTMLElement;
            const $date = $card.querySelector('.project-date') as HTMLElement;
            const $ordersLine = $card.querySelector('.project-orders-line') as HTMLElement;
            const $status = $card.querySelector('.project-status') as HTMLElement;
            const $waiting = $card.querySelector('.project-waiting') as HTMLElement;
            const $balance = $card.querySelector('.project-balance') as HTMLElement;
            const $view = $card.querySelector('.project-view') as HTMLAnchorElement;

            // The customer projects endpoint blanks proof.file, so getURLs() can
            // come back empty — the server-built main URL is the fallback.
            const proofs = project.quote?.quoteProofs ?? [];
            const latest = proofs[proofs.length - 1];
            const main = latest?.proof?.getURLs().main || latest?.proof?.main || '';
            const thumbnail = latest?.proof?.thumbnail;
            if (main) {
                $thumb.classList.remove('d-none');
                void Picture($thumb, main, { displayWidth: 84, alt: 'Latest proof' });
                const $picture = $thumb.parentElement;
                if ($picture instanceof HTMLPictureElement) {
                    $picture.classList.add('d-block', 'w-100', 'h-100');
                }
            } else if (thumbnail) {
                $thumb.classList.remove('d-none');
                $thumb.src = thumbnail;
            } else {
                $placeholder.classList.remove('d-none');
            }

            $name.textContent = project.name;
            $name.title = project.name;

            if (closedView) {
                $date.textContent = project.closedAt?.isValid ?
                    `Closed ${project.closedAt.toLocaleString(DateTime.DATE_MED)}` :
                    'Closed';
            } else {
                $date.textContent = `Updated ${project.updated.toLocaleString(DateTime.DATE_MED)}`;
            }

            $ordersLine.textContent = ProjectOrdersLine(project.orders);

            const own = !view.active || view.isOwn(project.userID);

            if (closedView) {
                $status.textContent = 'Closed';
                $status.classList.add(...ProjectStatusPillClasses('closed'));
            } else {
                $status.textContent = ProjectStatusLabel(project.status);
                $status.classList.add(...ProjectStatusPillClasses(project.status));

                if (own) {
                    if (project.waitingOn === 'you') {
                        $waiting.textContent = 'Waiting on you';
                        $waiting.classList.add('fw-bold', 'text-primary');
                        $waiting.classList.remove('d-none');
                    } else if (project.waitingOn === 'us') {
                        $waiting.textContent = 'With our art team';
                        $waiting.classList.add('text-muted');
                        $waiting.classList.remove('d-none');
                    }
                }
            }

            if (own) {
                if (project.balanceDue.gt(0)) {
                    $balance.textContent = `${DollarFormat(project.balanceDue)} due`;
                    $balance.classList.add('fw-bold');
                } else if (project.balanceDue.lt(0)) {
                    $balance.textContent = `${DollarFormat(project.balanceDue.abs())} credit`;
                } else {
                    $balance.textContent = 'No balance due';
                    $balance.classList.add('small', 'text-muted');
                }
            } else {
                $balance.classList.add('d-none');
            }

            if (view.active) {
                const $attribution = $card.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, project.userID);
                $attribution.classList.remove('d-none');
            }

            $view.href = view.withLens(`${Relref('/projects/details')}?id=${project.id ?? ''}`);

            return $card;
        },
        afterLoad: state => {
            if (state === 'failed') {
                $closedLinkRow.classList.add('d-none');
                return;
            }

            const orgName = view.organization?.name ?? '';

            if (state === 'empty' && view.active) {
                if (closedView) {
                    $closedEmptyTitle.textContent = `${orgName} has no closed projects`;
                    $closedEmptyBody.textContent = 'When a member closes a project, it moves here.';
                } else {
                    $emptyTitle.textContent = `No projects in ${orgName} yet`;
                    $emptyBody.textContent = `When someone in ${orgName} starts a project, it appears in this list.`;
                    $emptyCta.classList.add('d-none');
                }
                return;
            }

            if (state === 'no-match') {
                const search = pl.search().trim();
                if (view.active) {
                    $noMatchTitle.textContent = `Nothing in ${orgName} matches "${search}"`;
                    $noMatchBody.textContent = `Clear the search to see every project in ${orgName}.`;
                } else {
                    $noMatchTitle.textContent = `No projects match "${search}".`;
                }
            }
        },
    });
    pl.load();
}
